import React from 'react';

type Props = {
    currentStage?: string;
    width?: number;
    height?: number;
    showLabels?: boolean;
};

type FunnelStage = {
    id: string;
    lines: Array<string>;
};

const STAGES: Array<FunnelStage> = [
    { id: 'referral_created_not_signed_in', lines: ['ACCOUNT CREATED'] },
    { id: 'signed_in_successfully', lines: ['INSTALLED AND', 'SIGNED-IN'] },
    { id: 'completed_onboarding', lines: ['ONBOARDING', 'COMPLETE'] },
    { id: 'first_used_app', lines: ['ACTIVE'] },
];

const CURRENT_STAGE_FILL_COLORS: Record<string, string> = {
    referral_created_not_signed_in: '#FF6B6B',
    signed_in_successfully: '#FFD93D',
    completed_onboarding: '#FFD93D',
    first_used_app: '#5CE65C',
};

// Use the stage itself to give each current state its semantic colour.
function getStageFillColor(stageId: string, status: 'past' | 'current' | 'future'): string {
    switch (status) {
        case 'past':
            return '#74D7EE';
        case 'current':
            return CURRENT_STAGE_FILL_COLORS[stageId];
        case 'future':
        default:
            return '#E4E4E4';
    }
}

export function FunnelWidget({
    currentStage,
    width,
    height,
    showLabels = true,
}: Props) {
    const currentStageIndex = Math.max(
        STAGES.findIndex((stage) => stage.id === currentStage),
        0
    );

    const segmentWidth = 248;
    const segmentHeight = 60;
    const notch = 24;
    const gap = 16;
    const segmentOffset = segmentWidth - notch + gap;
    const totalWidth = segmentWidth + (segmentOffset * (STAGES.length - 1));
    const renderedWidth = width ?? totalWidth;
    const renderedHeight = height ?? segmentHeight;

    return (
        <div style={styles.container}>
            <div style={{ ...styles.frame, width: `${renderedWidth}px`, height: `${renderedHeight}px` }}>
                <svg
                    viewBox={`0 0 ${totalWidth} ${segmentHeight}`}
                    style={{ ...styles.svg, width: `${renderedWidth}px`, height: `${renderedHeight}px` }}
                    role='img'
                    aria-label='Client usage funnel'
                >
                    {STAGES.map((stage, index) => {
                        const x = index * segmentOffset;
                        const status =
                            index < currentStageIndex ? 'past' :
                            index === currentStageIndex ? 'current' :
                            'future';

                        const points = `${x},0 ${x + notch},${segmentHeight / 2} ${x},${segmentHeight} ${x + segmentWidth - notch},${segmentHeight} ${x + segmentWidth},${segmentHeight / 2} ${x + segmentWidth - notch},0`;

                        const centerX = x + (segmentWidth / 2);
                        const firstLineY = stage.lines.length > 1 ? 25 : 35;

                        return (
                            <g key={stage.id}>
                                <polygon points={points} fill={getStageFillColor(stage.id, status)} />
                                {showLabels && (
                                    <text
                                        x={centerX}
                                        y={firstLineY}
                                        textAnchor='middle'
                                        style={styles.label}
                                    >
                                        {stage.lines.map((line, lineIndex) => (
                                            <tspan
                                                key={line}
                                                x={centerX}
                                                dy={lineIndex === 0 ? 0 : 22}
                                            >
                                                {line}
                                            </tspan>
                                        ))}
                                    </text>
                                )}
                            </g>
                        );
                    })}
                </svg>
            </div>
        </div>
    );
}

const styles: Record<string, React.CSSProperties> = {
    container: {
        width: '100%',
        overflowX: 'auto',
    },
    frame: {
        flexShrink: 0,
    },
    svg: {
        display: 'block',
    },
    label: {
        fontSize: '16px',
        fontWeight: 600,
        fill: '#111111',
        letterSpacing: '0.02em',
    },
};
